import { subject } from "@casl/ability";
import axios from "axios";
import isEmpty from "lodash/isEmpty";

import { portalApi, TAGS } from "api/rtkApi";
import { isUUID } from "utils";

import getErrorDetails from "../../../api/helpers/getErrorDetails";
import {
  addPaginationAndSort,
  getPaginationInfoFromHeaders,
  ORDER_ASC,
  PaginatedList,
} from "../../../api/helpers/pagination";
import paramsSerializer from "../../../api/helpers/paramsSerializer";
import { SUBJECT_BANK } from "../../../models/role/subject";

import type { RTKMeta } from "models/RTKMeta";
import type { Order } from "../../../models/helpers";
import type { Bank } from "../models/Bank";

export interface GetBanksParams {
  page: number;
  limit: number;
  order: Order;
  orderBy: keyof Bank;
  search: string | null;
}

export async function getBanks(
  qsParams: GetBanksParams,
  options?: any
): Promise<PaginatedList<Bank>> {
  const { search } = qsParams;
  const params = { ...addPaginationAndSort<Bank>(qsParams) };

  if (!isEmpty(search)) {
    Object.assign(params, { search: search });
  }

  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: "/banks",
      params,
      paramsSerializer,
    });

    const { data = {}, headers: responseHeaders = {} } = response;

    const {
      totalItems,
      currentPage,
      currentLimit,
    } = getPaginationInfoFromHeaders(responseHeaders);

    return {
      list: (data ?? []).map((bank: Bank) => subject(SUBJECT_BANK, bank)),
      totalItems,
      currentPage,
      currentLimit,
    };
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function getBank(id: string, options?: any): Promise<Bank> {
  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: `/banks/${id}`,
    });
    const { data = {} } = response;

    return subject(SUBJECT_BANK, data);
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function patchBank(
  id: string,
  bank: Bank,
  options?: any
): Promise<Bank> {
  try {
    const response = await axios({
      ...options,
      method: "PUT",
      url: `/banks/${id}`,
      data: {
        ...bank,
        ...(bank.country ? { country: bank.country?.id } : {}),
      },
    });
    const { data = {} } = response;

    return await getBank(data.id, options);
  } catch (error) {
    throw getErrorDetails(error);
  }
}

type GetBanksForAutocompleteResponse = {
  banks: Bank[];
  totalItems?: number;
};

type GetBanksForAutocompleteRequest = {
  search?: string;
};

const BANKS_AUTOCOMPLETE_RESULTS_LIMIT = 100;

const extendedApi = portalApi.injectEndpoints({
  endpoints: (build) => ({
    getBanksForAutocomplete: build.query<
      GetBanksForAutocompleteResponse,
      GetBanksForAutocompleteRequest
    >({
      query: ({ search }) => ({
        url: "banks",
        params: {
          limit: BANKS_AUTOCOMPLETE_RESULTS_LIMIT,
          "order[name]": ORDER_ASC,
          page: 1,
          ...(typeof search === "string" && isUUID(search)
            ? {
                id: search,
              }
            : { search }),
        },
      }),
      providesTags: (_, __, { search }) => [
        { type: TAGS.BANKS_AUTOCOMPLETE, id: search },
      ],
      transformResponse: (data: Bank[], meta: RTKMeta) => {
        const totalItems = Number(meta.response.headers.get("total-items"));

        return {
          totalItems,
          banks: data,
        };
      },
    }),
  }),
});

export const { useLazyGetBanksForAutocompleteQuery } = extendedApi;
